import { useMemo, useState } from "react";
import { Link } from "react-router-dom";
import { RotateCcw, ArrowRight, Mail, MessageCircle } from "lucide-react";
import SEO from "../components/SEO";
import Breadcrumbs from "../components/layout/Breadcrumbs";
import CTASection from "../components/blocks/CTASection";
import { ProgrammeCard } from "../components/blocks/Cards";
import { ASSESSMENT_QUESTIONS, ASSESSMENT_ROLES, ROLE_TO_AUDIENCE, DOMAIN_LABELS, DOMAIN_GUIDANCE } from "../data/assessment";
import { PROGRAMMES } from "../data/programmes";

export default function AssessmentPage() {
  const [step, setStep] = useState(0); // 0 = role, 1..8 = questions, 9 = result
  const [role, setRole] = useState("");
  const [scores, setScores] = useState({});
  const total = ASSESSMENT_QUESTIONS.length;

  const answer = (scoresForOption) => {
    setScores((prev) => {
      const next = { ...prev };
      Object.entries(scoresForOption).forEach(([k, v]) => {
        next[k] = (next[k] || 0) + v;
      });
      return next;
    });
    setStep((s) => s + 1);
  };

  const reset = () => {
    setStep(0);
    setRole("");
    setScores({});
  };

  const ranked = useMemo(
    () => Object.entries(scores).sort((a, b) => b[1] - a[1]),
    [scores]
  );

  const recommendations = useMemo(() => {
    if (ranked.length === 0) return [];
    const audience = ROLE_TO_AUDIENCE[role];
    const topDomains = ranked.slice(0, 2).map(([k]) => DOMAIN_LABELS[k]);
    const matches = PROGRAMMES.filter((p) => topDomains.includes(p.capability));
    return matches.sort((a, b) => (b.audience === audience ? 1 : 0) - (a.audience === audience ? 1 : 0)).slice(0, 3);
  }, [ranked, role]);

  const maxScore = ranked.length ? ranked[0][1] : 1;

  const resultText = useMemo(() => {
    if (ranked.length === 0) return "";
    const lines = ranked.slice(0, 3).map(([k], i) => `${i + 1}. ${DOMAIN_LABELS[k]} — ${DOMAIN_GUIDANCE[k]}`);
    const recs = recommendations.map((p) => `• ${p.title} (${p.category}, ${p.duration})`);
    return [
      "My Vantage Capability Assessment result",
      role ? `Profile: ${role}` : null,
      "",
      "Priority capability domains:",
      ...lines,
      "",
      "Recommended starting points:",
      ...recs,
      "",
      `Taken at: ${window.location.origin}/assessment`,
    ].filter(Boolean).join("\n");
  }, [ranked, role, recommendations]);

  const shareMailHref = `mailto:?subject=${encodeURIComponent("My Vantage Capability Assessment result")}&body=${encodeURIComponent(resultText)}`;
  const shareWaHref = `https://wa.me/?text=${encodeURIComponent(resultText)}`;

  return (
    <>
      <SEO
        title="Capability Assessment — Find Your Learning Path | The Vantage Institute™"
        description="A short orientation assessment that maps your priorities to Vantage capability domains and recommends a learning path."
        path="/assessment"
      />
      <Breadcrumbs items={[{ label: "Home", to: "/" }, { label: "Learn", to: "/learning" }, { label: "Capability Assessment" }]} />

      <section className="bg-navy">
        <div className="container-x py-16 sm:py-20">
          <p className="eyebrow-light">Capability Assessment</p>
          <h1 className="mt-4 max-w-3xl font-display text-4xl font-semibold leading-tight text-white sm:text-5xl">
            Find your learning path.
          </h1>
          <span className="gold-rule mt-8 !w-24" aria-hidden="true" />
          <p className="mt-8 max-w-2xl text-base leading-relaxed text-platinum md:text-lg">
            Nine quick questions. The result maps your priorities to Vantage capability domains and recommends
            where to start. An orientation tool, not a psychometric instrument — nothing is stored or sent.
          </p>
        </div>
      </section>

      <section className="container-x py-14 sm:py-20" data-testid="assessment">
        <div className="mx-auto max-w-2xl">
          {step <= total && (
            <p className="font-sub text-xs font-semibold uppercase tracking-[0.14em] text-gold-deep" role="status" data-testid="assessment-progress">
              {step === 0 ? "Step 1 of " + (total + 1) : `Question ${step} of ${total}`}
            </p>
          )}
          <div className="mt-2 h-1 w-full bg-platinum" aria-hidden="true">
            <div className="h-1 bg-gold transition-[width] duration-300" style={{ width: `${Math.min((step / total) * 100, 100)}%` }} />
          </div>

          {step === 0 && (
            <div className="mt-10" data-testid="assessment-role-step">
              <h2 className="font-sub text-xl font-semibold text-navy">First — which best describes you?</h2>
              <div className="mt-6 grid gap-3">
                {ASSESSMENT_ROLES.map((r) => (
                  <button
                    key={r}
                    type="button"
                    onClick={() => { setRole(r); setStep(1); }}
                    className="card card-hover flex min-h-[52px] items-center justify-between text-left font-sub text-sm font-semibold text-navy"
                    data-testid={`assessment-role-${r.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "")}`}
                  >
                    {r}
                    <ArrowRight size={16} aria-hidden="true" className="text-gold-deep" />
                  </button>
                ))}
              </div>
            </div>
          )}

          {step >= 1 && step <= total && (
            <div className="mt-10" data-testid={`assessment-question-${step}`}>
              <h2 className="font-sub text-xl font-semibold leading-snug text-navy">
                {ASSESSMENT_QUESTIONS[step - 1].q}
              </h2>
              <div className="mt-6 grid gap-3">
                {ASSESSMENT_QUESTIONS[step - 1].options.map((o) => (
                  <button
                    key={o.label}
                    type="button"
                    onClick={() => answer(o.scores)}
                    className="card card-hover flex min-h-[52px] items-center text-left font-body text-sm leading-relaxed text-navy"
                    data-testid={`assessment-option-${o.label.toLowerCase().replace(/[^a-z]+/g, "-").replace(/-$/, "").slice(0, 40)}`}
                  >
                    {o.label}
                  </button>
                ))}
              </div>
            </div>
          )}

          {step > total && (
            <div className="mt-10" data-testid="assessment-results">
              <p className="eyebrow">Your Result{role ? ` — ${role}` : ""}</p>
              <h2 className="section-title mt-3">Your priority capability domains</h2>
              <div className="mt-8 space-y-5">
                {ranked.slice(0, 3).map(([key, score], i) => (
                  <div key={key} data-testid={`assessment-domain-${key}`}>
                    <div className="flex items-baseline justify-between gap-4">
                      <p className="font-sub text-sm font-semibold text-navy">
                        {i + 1}. {DOMAIN_LABELS[key]}
                      </p>
                    </div>
                    <div className="mt-2 h-1.5 w-full bg-platinum" aria-hidden="true">
                      <div className="h-1.5 bg-gold" style={{ width: `${Math.max((score / maxScore) * 100, 12)}%` }} />
                    </div>
                    <p className="mt-2.5 text-sm leading-relaxed text-slate">{DOMAIN_GUIDANCE[key]}</p>
                  </div>
                ))}
              </div>

              <h3 className="mt-12 font-sub text-lg font-semibold text-navy">Recommended starting points</h3>
              <span className="gold-rule mt-3 !w-10" aria-hidden="true" />
              {recommendations.length > 0 ? (
                <div className="mt-6 grid gap-5">
                  {recommendations.map((p) => (
                    <ProgrammeCard key={p.slug} programme={p} testId={`assessment-rec-${p.slug}`} />
                  ))}
                </div>
              ) : (
                <p className="mt-6 text-sm text-slate">
                  Explore the full catalogue to find the right starting point.
                </p>
              )}
              <div className="mt-8 flex flex-col gap-3 sm:flex-row">
                <Link to="/programmes" className="btn-navy" data-testid="assessment-cta-catalogue">Explore All Programmes</Link>
                <button type="button" onClick={reset} className="btn-outline-navy" data-testid="assessment-retake">
                  <RotateCcw size={15} aria-hidden="true" />
                  Retake Assessment
                </button>
              </div>
              <div className="mt-6 border-t border-platinum pt-6">
                <p className="font-sub text-xs font-semibold uppercase tracking-[0.14em] text-slate">Keep your result</p>
                <div className="mt-3 flex flex-col gap-3 sm:flex-row">
                  <a href={shareMailHref} className="btn-outline-navy !min-h-[44px] !px-5 !py-2 text-xs" data-testid="assessment-share-email">
                    <Mail size={15} aria-hidden="true" />
                    Email My Result
                  </a>
                  <a href={shareWaHref} target="_blank" rel="noopener noreferrer" className="btn-outline-navy !min-h-[44px] !px-5 !py-2 text-xs" data-testid="assessment-share-whatsapp">
                    <MessageCircle size={15} aria-hidden="true" />
                    Send to My WhatsApp
                  </a>
                </div>
              </div>
              <p className="mt-6 text-xs leading-relaxed text-slate">
                This assessment is an orientation tool — a structured reflection, not a measurement. Results stay
                on your device; nothing is stored or transmitted.
              </p>
            </div>
          )}
        </div>
      </section>

      {step > total && (
        <CTASection
          title="Discuss your result with the Institute."
          text="A short conversation can turn an orientation result into a concrete learning plan."
          primaryLabel="Talk to Vantage"
          primaryTo="/contact"
          secondaryLabel="Explore the Thinking Toolbox™"
          secondaryTo="/thinking-toolbox"
          testId="assessment-cta"
        />
      )}
    </>
  );
}
